/**
 * App component — designv2 SPA shell. Owns the view state, fetches read-side
 * data, wires modals and the Nav. Where a real API exists (people, job-roles)
 * we use it; everything else falls back to the mock seeds in `data.ts` until
 * its capability slice lands.
 */
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import type {
  ActualsByProject,
  CostRate,
  CostsByPerson,
  Person,
  Project,
  ProjectInput,
  SysRole,
} from "@shared/types";
import { DebugMenu } from "./components/DebugMenu";
import { TempPasswordReveal } from "./components/TempPasswordReveal";
import { JOB_ROLES_SEED } from "./data";
import { apiFetch } from "./lib/api";
import { clearTokens } from "./lib/auth-storage";
import { loadCostsForPeople } from "./lib/costs-api";
import {
  type EffectiveRole,
  applyRoleSwitch,
  getEffectiveRole,
  setEffectiveRole,
} from "./lib/effective-role";
import { listJobRoles } from "./lib/job-roles-api";
import { type SpaMode, getMyRange, patchMyMode, patchMyRange } from "./lib/me-api";
import { createPerson, deletePerson, getPerson, listPeople, updatePerson } from "./lib/people-api";
import {
  createProject,
  deleteProject,
  getProject,
  listProjects,
  updateProject,
} from "./lib/projects-api";
import { sortPeopleByStatus, sortProjectsByStatus } from "./lib/status-sort";
import { usePersistedState } from "./lib/viewState";
import { ImportActualsModal } from "./modal_import";
import { PersonModal, type PersonModalSaveInput, ProjectModal } from "./modals";
import { ThemeCtx } from "./theme";
import { DEFAULT_RANGE_END, DEFAULT_RANGE_START, DOLMIT } from "./tokens";
import { AdminView } from "./views/views_admin";
import { BenchView } from "./views/views_bench";
import { BudgetView } from "./views/views_budget";
import { DashboardView, ReportsView } from "./views/views_dashboard";
import { PersonDetailView, ProjectDetailView } from "./views/views_grid";
import { Nav, ProjectsView, UsersView } from "./views/views_list";

export type View =
  | "dashboard"
  | "project_detail"
  | "person_detail"
  | "bench"
  | "projects"
  | "users"
  | "budget"
  | "reports"
  | "admin";

export const VIEW_TO_PATH: Record<View, string> = {
  dashboard: "/",
  project_detail: "/projects-view",
  person_detail: "/person-view",
  bench: "/bench",
  budget: "/budget",
  reports: "/reports",
  projects: "/project-setup",
  users: "/people-setup",
  admin: "/admin",
};

const PATH_TO_VIEW: Record<string, View> = Object.fromEntries(
  Object.entries(VIEW_TO_PATH).map(([v, p]) => [p, v as View]),
);

function viewFromPath(pathname: string): View {
  return PATH_TO_VIEW[pathname] ?? "dashboard";
}

const ALLOWED_VIEWS: Record<string, ReadonlySet<View>> = {
  admin: new Set<View>([
    "dashboard",
    "project_detail",
    "person_detail",
    "bench",
    "projects",
    "users",
    "budget",
    "reports",
    "admin",
  ]),
  lead: new Set<View>([
    "dashboard",
    "project_detail",
    "person_detail",
    "bench",
    "budget",
    "reports",
  ]),
  pm: new Set<View>(["dashboard", "project_detail", "person_detail", "bench", "budget", "reports"]),
  accountant: new Set<View>(["budget", "reports"]),
};

const defaultViewFor = (r: string): View => (r === "accountant" ? "budget" : "dashboard");

type PersonWithOptionalCostRates = Person & { costRates?: CostRate[] };

/**
 * Initial-hydration only. The `people` list mutates over time (PATCH
 * responses omit `costRates`), so re-deriving from the live array would
 * zero out cost maps for edited people. After hydration the `costs` state
 * is owned by `onSaveCost`, which re-fetches the affected person's chain.
 */
export function deriveCostsByPersonFromPeople(
  rows: readonly PersonWithOptionalCostRates[],
): CostsByPerson {
  const out: CostsByPerson = {};
  for (const person of rows) {
    if (Array.isArray(person.costRates)) {
      out[String(person.id)] = person.costRates;
    }
  }
  return out;
}

export interface AppProps {
  /** Optional callback to log the user out — passed in by the route wrapper. */
  onLogout?: () => void;
}

export function App({ onLogout: onLogoutProp }: AppProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Derive the active view from the URL — App stays mounted across route
  // changes so people/projects/etc. don't refetch on every tab click.
  const view = viewFromPath(location.pathname);
  const setView = (v: View) => {
    void navigate({ to: VIEW_TO_PATH[v] });
  };
  const [people, setPeople] = useState<Person[]>([]);
  const [costs, setCosts] = useState<CostsByPerson>({});
  const [projects, setProjects] = useState<Project[]>([]);
  const [actuals, setActuals] = useState<ActualsByProject>({});
  const [suggestions, setSuggestions] = useState<string[]>(() => [...JOB_ROLES_SEED]);
  const [personModal, setPersonModal] = useState<Person | "new" | null>(null);
  const [projectModal, setProjectModal] = useState<Project | "new" | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = usePersistedState<number[]>(
    "projects",
    "selectedIds",
    [1, 2],
  );
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [costRetry, setCostRetry] = useState<{
    personId: number;
    body: unknown;
  } | null>(null);
  const [costRetryBusy, setCostRetryBusy] = useState(false);
  /** The authenticated user's real sysRole (from /api/me). Distinct from
   *  the demo `role` state below — that one drives the view-as-role
   *  preview; this one decides whether the user is allowed to flip it. */
  const [authedSysRole, setAuthedSysRole] = useState<string | null>(null);
  const [authedUserId, setAuthedUserId] = useState<number | null>(null);
  const [debugTools, setDebugTools] = useState(false);

  // Range/mode seed from /api/me/range on mount. `userEditedRangeRef`
  // guards against a slow GET clobbering an in-progress user edit.
  // Setters bail on no-op (PeriodPicker fires `onChange` on every mount
  // with its initial props, which would otherwise PATCH the server with
  // the values it just rendered).
  const [rangeStart, setRangeStart] = useState<string>(DEFAULT_RANGE_START);
  const [rangeEnd, setRangeEnd] = useState<string>(DEFAULT_RANGE_END);
  const [mode, setModeState] = useState<SpaMode>("planned");
  const userEditedRangeRef = useRef(false);
  const setRange = (s: string, e: string) => {
    if (s === rangeStart && e === rangeEnd) return;
    userEditedRangeRef.current = true;
    setRangeStart(s);
    setRangeEnd(e);
    void patchMyRange({ rangeStart: s, rangeEnd: e }).catch(() => {});
  };
  const setMode = (m: SpaMode) => {
    if (m === mode) return;
    userEditedRangeRef.current = true;
    setModeState(m);
    void patchMyMode(m).catch(() => {});
  };
  const [effectiveRole, setEffectiveRoleState] = useState<EffectiveRole | null>(() =>
    getEffectiveRole(),
  );
  const role = effectiveRole ?? authedSysRole ?? "admin";
  const setRole = (r: string) => {
    if (r === "admin" || r === "pm" || r === "lead" || r === "accountant") {
      applyRoleSwitch(r);
    }
  };
  const [density] = useState<"compact" | "cozy" | "comfortable">("compact");

  // Bounce off forbidden tabs after role switch — URL-driven now.
  useEffect(() => {
    // ALLOWED_VIEWS["admin"] is statically populated at module init, so
    // either the role-specific lookup or the admin fallback is defined.
    const allowed = ALLOWED_VIEWS[role] ?? ALLOWED_VIEWS["admin"] ?? new Set<View>();
    if (!allowed.has(view)) {
      void navigate({ to: VIEW_TO_PATH[defaultViewFor(role)] });
    }
  }, [role, view, navigate]);

  useEffect(() => {
    if (authedSysRole && authedSysRole !== "admin" && effectiveRole !== null) {
      setEffectiveRole(null);
      setEffectiveRoleState(null);
    }
  }, [authedSysRole, effectiveRole]);

  // Clear a pending cost retry when the user re-opens the same person —
  // the new save attempt supersedes the previous one.
  useEffect(() => {
    if (
      costRetry &&
      personModal !== null &&
      personModal !== "new" &&
      personModal.id === costRetry.personId
    ) {
      setCostRetry(null);
    }
  }, [personModal, costRetry]);

  // Mount-time fetches run in parallel — each handler is self-contained
  // (sets its own state, swallows its own error). Cost rates arrive embedded
  // in /api/people for cost-cleared roles and are absent for PMs; /api/job-
  // roles failure falls back to JOB_ROLES_SEED already in state; /api/me
  // failure redirects via AuthenticatedLayout.
  useEffect(() => {
    let cancelled = false;
    async function loadPeople(): Promise<number[]> {
      try {
        const rows = await listPeople();
        if (cancelled) return [];
        setPeople(rows);
        setCosts(deriveCostsByPersonFromPeople(rows));
        setLoadError(null);
        return rows.map((p) => p.id);
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message || "Failed to load people");
        return [];
      }
    }
    async function loadProjs() {
      try {
        const projRows = await listProjects();
        if (cancelled) return;
        setProjects(projRows);
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message || "Failed to load projects");
      }
    }
    async function loadRoles() {
      try {
        const list = await listJobRoles();
        if (!cancelled && list.length) setSuggestions(list);
      } catch {}
    }
    async function loadMe() {
      try {
        const res = await apiFetch("/api/me");
        if (cancelled) return;
        const body = (await res.json()) as {
          id: number;
          sysRole: string;
          debugTools?: boolean;
        };
        setAuthedSysRole(body.sysRole);
        setAuthedUserId(body.id);
        setDebugTools(body.debugTools === true);
      } catch {}
    }
    async function loadRange() {
      try {
        const range = await getMyRange();
        if (cancelled || !range) return;
        if (userEditedRangeRef.current) return;
        setRangeStart(range.rangeStart);
        setRangeEnd(range.rangeEnd);
        setModeState(range.mode);
      } catch {}
    }
    async function loadActuals() {
      try {
        const res = await apiFetch("/api/actuals");
        if (cancelled || !res.ok) return;
        setActuals((await res.json()) as ActualsByProject);
      } catch {}
    }
    async function load() {
      // Load people once; cost-rate hydration is embedded in /api/people for
      // cost-cleared callers and omitted entirely for non-cost roles.
      await loadPeople();
      await Promise.allSettled([loadProjs(), loadRoles(), loadMe(), loadRange(), loadActuals()]);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function refetchActuals(): Promise<void> {
    try {
      const res = await apiFetch("/api/actuals");
      if (res.ok) {
        const body = (await res.json()) as ActualsByProject;
        setActuals(body);
      }
    } catch {
      // ignore
    }
  }

  const currentUserId = authedUserId ?? 0;

  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const openPerson = (pid: number) => {
    setSelectedPersonId(pid);
    setView("person_detail");
  };
  const openProject = (projectId: number) => {
    setSelectedProjectIds([projectId]);
    setView("project_detail");
  };

  async function savePerson(input: PersonModalSaveInput): Promise<Person | undefined> {
    setSaveError(null);
    const editing = personModal !== null && personModal !== "new" ? personModal : null;
    try {
      if (editing) {
        const result = await updatePerson(editing.id, input);
        setPeople((prev) =>
          sortPeopleByStatus(prev.map((p) => (p.id === result.person.id ? result.person : p))),
        );
        if (result.temporaryPassword) setTempPassword(result.temporaryPassword);
        return result.person;
      }
      const result = await createPerson(input);
      setPeople((prev) => sortPeopleByStatus([...prev, result.person]));
      if (result.temporaryPassword) setTempPassword(result.temporaryPassword);
      return result.person;
    } catch (err) {
      setSaveError((err as Error).message);
      return undefined;
    }
  }

  /**
   * Refresh the in-memory cost chain for one person after the PersonModal
   * editor commits its row diffs. The editor itself owns row-level POST/
   * PATCH/DELETE via cost-rates-api; App.tsx just re-fetches to update the
   * derived views (budget drilldown, dashboard rollup, list cost column).
   */
  async function onSaveCost(personId: number, _body?: unknown): Promise<void> {
    const map = await loadCostsForPeople([personId]);
    setCosts((prev) => ({ ...prev, ...map }));
  }

  function onCostSaveFailed(personId: number, body: unknown) {
    setCostRetry({ personId, body });
  }

  async function retryCostSave() {
    if (!costRetry || costRetryBusy) return;
    setCostRetryBusy(true);
    try {
      await onSaveCost(costRetry.personId);
      setCostRetry(null);
    } catch {
      // Toast remains; user can retry again or dismiss.
    } finally {
      setCostRetryBusy(false);
    }
  }

  async function onUpdateRoles(id: number, next: string[]) {
    try {
      const result = await updatePerson(id, { roles: next });
      setPeople((prev) => prev.map((q) => (q.id === id ? result.person : q)));
    } catch (err) {
      console.error("updatePerson(roles) failed:", err);
    }
  }

  async function saveProject(input: ProjectInput) {
    setSaveError(null);
    const editing = projectModal !== null && projectModal !== "new" ? projectModal : null;
    try {
      if (editing) {
        const result = await updateProject(editing.id, input);
        setProjects((prev) =>
          sortProjectsByStatus(prev.map((p) => (p.id === result.id ? result : p))),
        );
      } else {
        const result = await createProject(input);
        setProjects((prev) => sortProjectsByStatus([...prev, result]));
      }
      setProjectModal(null);
    } catch (err) {
      setSaveError((err as Error).message || "Failed to save project");
    }
  }

  async function handleDeleteProject(id: number) {
    setSaveError(null);
    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setProjectModal(null);
    } catch (err) {
      setSaveError((err as Error).message || "Failed to delete project");
    }
  }

  async function handleDeletePerson(id: number) {
    setSaveError(null);
    try {
      await deletePerson(id);
      setPeople((prev) => prev.filter((p) => p.id !== id));
      setPersonModal(null);
    } catch (err) {
      setSaveError((err as Error).message || "Failed to delete person");
    }
  }

  async function onLogout() {
    if (onLogoutProp) {
      onLogoutProp();
      return;
    }
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      /* best-effort */
    }
    clearTokens();
    window.location.assign("/login");
  }

  const user =
    (authedUserId !== null && people.find((p) => p.id === authedUserId)) ||
    people.find((p) => p.sysRole === "admin") ||
    people[0];

  const ctx = { C: DOLMIT, dark: false, density };

  return (
    <ThemeCtx.Provider value={ctx}>
      {loadError && !user && (
        <main style={{ maxWidth: 480, margin: "10vh auto" }}>
          <h1 style={{ fontSize: 22, marginBottom: 12, color: DOLMIT.navyDeep }}>
            Couldn't load people
          </h1>
          <p style={{ color: DOLMIT.slate, marginBottom: 16 }}>{loadError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ padding: "8px 16px" }}
          >
            Retry
          </button>
        </main>
      )}
      {user && (
        <Nav
          view={view}
          setView={(v) => setView(v as View)}
          user={user}
          role={role}
          setRole={setRole}
          people={people}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          setRange={setRange}
          mode={mode}
          setMode={setMode}
          onLogout={onLogout}
          canSwitchRole={authedSysRole === "admin"}
          userDropdownExtra={
            <DebugMenu
              debugTools={debugTools}
              userRole={authedSysRole ?? ""}
              userId={authedUserId}
            />
          }
        />
      )}

      {view === "dashboard" && (
        <DashboardView
          projects={projects}
          people={people}
          costs={costs}
          mode={mode}
          actuals={actuals}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          role={role}
          currentUserId={currentUserId}
          setView={setView as (v: string) => void}
          setSelectedProjectIds={setSelectedProjectIds}
          openPerson={openPerson}
        />
      )}

      {view === "projects" && (
        <ProjectsView
          projects={projects}
          people={people}
          costs={costs}
          role={role}
          currentUserId={currentUserId}
          mode={mode}
          actuals={actuals}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onOpen={(p) => {
            setSelectedProjectIds([p.id]);
            setView("project_detail");
          }}
          onEdit={(p) => {
            void getProject(p.id)
              .then((detail) => setProjectModal(detail))
              .catch(() => setProjectModal(p));
          }}
          openPerson={openPerson}
          onNew={() => setProjectModal("new")}
        />
      )}

      {view === "users" && (
        <UsersView
          people={people}
          costs={costs}
          projects={projects}
          role={role}
          suggestions={suggestions}
          onUpdateRoles={onUpdateRoles}
          openPerson={openPerson}
          onEdit={(p) => {
            void getPerson(p.id)
              .then((detail) => setPersonModal(detail))
              .catch(() => setPersonModal(p));
          }}
          onNew={() => setPersonModal("new")}
        />
      )}

      {view === "project_detail" && (
        <ProjectDetailView
          selectedIds={selectedProjectIds}
          setSelectedIds={setSelectedProjectIds}
          people={people}
          projects={projects}
          setProjects={setProjects}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          mode={mode}
          actuals={actuals}
          setActuals={setActuals}
          role={role}
          openPerson={openPerson}
          onEdit={(p: Project) => setProjectModal(p)}
        />
      )}

      {view === "person_detail" && (
        <PersonDetailView
          projects={projects}
          people={people}
          setProjects={setProjects}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          mode={mode}
          actuals={actuals}
          selectedPersonId={selectedPersonId}
          setSelectedPersonId={setSelectedPersonId}
          role={role}
          openProject={openProject}
        />
      )}

      {view === "budget" && (
        <BudgetView
          projects={projects}
          people={people}
          costs={costs}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          mode={mode}
          actuals={actuals}
          role={role}
          currentUserId={currentUserId}
          openPerson={openPerson}
        />
      )}

      {view === "reports" && (
        <ReportsView
          projects={projects}
          people={people}
          costs={costs}
          actuals={actuals}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          role={role}
          currentUserId={currentUserId}
          openPerson={openPerson}
          setImportOpen={setImportOpen}
        />
      )}

      {view === "bench" && (
        <BenchView
          projects={projects}
          people={people}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          role={role}
          currentUserId={currentUserId}
          openPerson={openPerson}
        />
      )}

      {view === "admin" && <AdminView role={role} />}

      {personModal && (
        <PersonModal
          key={personModal === "new" ? "new" : personModal.id}
          person={personModal === "new" ? null : personModal}
          currentUserRole={(authedSysRole ?? role) as SysRole}
          suggestions={suggestions}
          costs={costs}
          projects={projects}
          onClose={() => {
            setPersonModal(null);
            setSaveError(null);
          }}
          onSave={savePerson}
          onSaveCost={onSaveCost}
          onCostSaveFailed={onCostSaveFailed}
          saveError={saveError}
          {...(personModal !== "new" ? { onDelete: () => handleDeletePerson(personModal.id) } : {})}
        />
      )}

      {costRetry && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            maxWidth: 360,
            background: DOLMIT.surface,
            border: `1px solid ${DOLMIT.rBd}`,
            borderRadius: 8,
            padding: "10px 14px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            zIndex: 1100,
          }}
        >
          <span style={{ fontSize: 13, color: DOLMIT.r, flex: 1 }}>
            {(() => {
              const target = people.find((p) => p.id === costRetry.personId);
              const who = target?.name ?? `person #${costRetry.personId}`;
              return `Cost for ${who} didn't save.`;
            })()}
          </span>
          <button
            type="button"
            onClick={() => void retryCostSave()}
            disabled={costRetryBusy}
            style={{
              padding: "4px 10px",
              borderRadius: 4,
              border: `1px solid ${DOLMIT.line}`,
              background: DOLMIT.surface,
              cursor: costRetryBusy ? "wait" : "pointer",
              fontSize: 12,
            }}
          >
            {costRetryBusy ? "Retrying…" : "Retry"}
          </button>
          <button
            type="button"
            onClick={() => setCostRetry(null)}
            aria-label="Dismiss"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
              color: DOLMIT.slateMut,
            }}
          >
            ×
          </button>
        </div>
      )}

      {projectModal && (
        <ProjectModal
          key={projectModal === "new" ? "new" : projectModal.id}
          project={projectModal === "new" ? null : projectModal}
          people={people}
          onClose={() => {
            setProjectModal(null);
            setSaveError(null);
          }}
          onSave={saveProject}
          {...(projectModal !== "new"
            ? { onDelete: () => handleDeleteProject(projectModal.id) }
            : {})}
          currentUserRole={role}
          saveError={saveError}
        />
      )}

      {importOpen && (
        <ImportActualsModal
          projects={projects}
          people={people}
          currentUserId={currentUserId}
          onCommitted={() => void refetchActuals()}
          onClose={() => setImportOpen(false)}
        />
      )}

      {tempPassword !== null && (
        <TempPasswordReveal password={tempPassword} onClose={() => setTempPassword(null)} />
      )}
    </ThemeCtx.Provider>
  );
}
